import api from "./api";

export type InvitationStatus = "pending" | "accepted" | "expired" | "cancelled";

export type Invitation = {
  id: string;
  email: string;
  role_code: string;
  invited_by_id: string;
  club_id: string | null;
  team_id: string | null;
  athlete_name?: string | null;
  athlete_user_id?: string | null;
  token: string;
  status: InvitationStatus;
  expires_at: string;
  accepted_at: string | null;
  accepted_by_id: string | null;
  created_at: string;
  updated_at: string;
  invited_by?: {
    id: string;
    email?: string;
    full_name: string | null;
  };
  accepted_by?: {
    id: string;
    email: string;
    full_name: string | null;
  } | null;
  club?: {
    id: string;
    name: string;
    logo_url?: string | null;
  } | null;
  team?: {
    id: string;
    name: string;
    age_group?: string | null;
  } | null;
  // Present on the create response: whether the invitation email was actually
  // delivered. false means the invitation was saved but the mail did not send.
  email_sent?: boolean;
};

/**
 * Create a new invitation
 */
export async function createInvitation(payload: {
  email: string;
  /**
   * "parent" is how an athlete is invited when the club has no email for the
   * child — which is the normal case for minors. The invitation then carries
   * the child's name, and the parent creates the account into the invited team.
   */
  role_code: "coach" | "athlete" | "parent" | "coordinator";
  club_id?: string;
  team_id?: string;
  /** Required for a parent invitation: which child the club means. */
  athlete_name?: string;
  /** Staff only. Required for a coordinator — a coordinator with no sport
   *  coordinates nothing; optional for a coach, who can be assigned later. */
  sport_id?: string;
}) {
  const { data } = await api.post<Invitation>("/invitations", payload);
  return data;
}

/**
 * Get invitations sent by current user
 */
export async function getSentInvitations() {
  const { data } = await api.get<Invitation[]>("/invitations/sent");
  return data;
}

/**
 * Every invitation for a club, whoever sent it.
 *
 * `getSentInvitations` is scoped to the signed-in sender, which is why a club
 * admin could not see invitations a coordinator sent — or their co-admin's —
 * and the pending list looked empty while people were waiting.
 */
export async function getClubInvitations(
  clubId: string,
  filters?: { status?: InvitationStatus; role?: string }
) {
  const { data } = await api.get<(Invitation & { sport_name?: string | null })[]>(
    `/invitations/club/${clubId}`,
    { params: { ...(filters?.status ? { status: filters.status } : {}), ...(filters?.role ? { role: filters.role } : {}) } }
  );
  return data;
}

/**
 * Activate an invited person yourself, with a password you choose.
 *
 * The account comes out fully active — the email counts as confirmed, because
 * the club typed that address and is vouching for it. Pass the password on:
 * the person never received one.
 */
export async function activateInvitation(
  invitationId: string,
  payload: { password: string; full_name?: string }
) {
  const { data } = await api.post<{
    ok: boolean;
    existing_account: boolean;
    user_id: string;
    email?: string;
    message: string;
  }>(`/invitations/${invitationId}/activate`, payload);
  return data;
}

/**
 * Get invitation details by token (public endpoint)
 */
export async function getInvitationByToken(token: string) {
  const { data } = await api.get<Invitation>(`/invitations/${token}`);
  return data;
}

/**
 * Accept an invitation
 */
export async function acceptInvitation(
  token: string,
  payload: {
    password: string;
    full_name?: string;
  }
) {
  const { data } = await api.post<{
    // New-account path returns a session; existing-account path returns a flag
    // and no session (the user signs in with their existing password).
    token?: string;
    user?: {
      id: string;
      email: string;
      name: string | null;
    };
    existing_account?: boolean;
    message?: string;
    /** "add_child" when a parent must still create the child account. */
    next?: string | null;
    invitation_token?: string | null;
    athlete_name?: string | null;
  }>(`/invitations/${token}/accept`, payload);
  return data;
}

/**
 * Cancel an invitation
 */
export async function cancelInvitation(invitationId: string) {
  const { data } = await api.delete<{ message: string }>(`/invitations/${invitationId}`);
  return data;
}
